import React, { useEffect, useRef, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import qrcode from "../../../images/qr.png";
import classNames from "classnames";
import styles from "../invoice/Invoice.module.scss";
import { Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getSalesByIdSlice } from "../../../store/slices/sales";

const ReceiptInvoice = () => {
  const dispatch = useDispatch();
  const [showInvoice, setShowInvoice] = useState(false);
  const invoiceRef = useRef();
  const { clinicInfo } = useSelector((state) => state.clinic);
  const clinic = clinicInfo && clinicInfo?.length > 0 ? clinicInfo[0] : [];
  const { salesById } = useSelector((state) => state.sales);

  const receiptId = sessionStorage.getItem("receiptId");
  const { token } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    paidAmount: 0,
    totalAmount: 0,
    totalAfterDiscount: 0,
    dueAmount: 0,
  });
  useEffect(() => {
    if (receiptId) {
      dispatch(getSalesByIdSlice({ id: receiptId, token }));
    }
  }, [dispatch, receiptId, token]);

  const downloadInvoice = async () => {
    setShowInvoice(true);
    setTimeout(async () => {
      const input = invoiceRef.current;
      const canvas = await html2canvas(input, {
         scale: 2,
  useCORS: true,
  allowTaint: false,
  imageTimeout: 15000
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pageWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save("invoice.pdf");
      setShowInvoice(false);
    }, 500); // slight delay to ensure DOM is rendered
  };

  useEffect(() => {
    if (salesById) {
      const appointmentDetails =
        salesById?.sale?.invoiceId?.[0]?.appointmentDetails || {};

      const isClient = salesById?.sale?.invoiceId?.[0]?.isClient === "Yes";
      const healthScreeningItems =
        appointmentDetails?.healthScreeningDetails || [];
      const physiotherapyItems = appointmentDetails?.physiotherapyService || [];
      const radioServices = appointmentDetails?.radioServices || [];
      const healthPackageItems = appointmentDetails?.healthPackageDetails || [];
      const skinServices = appointmentDetails?.skinServices || [];
      const otherServices = [
        ...(appointmentDetails?.tests || []),
        ...(appointmentDetails?.profiles || []),
        ...(appointmentDetails?.consultationDetails || []),
        ...(appointmentDetails?.dentalServices || []),
        ...(appointmentDetails?.generalServices || []),
      ];

      let totalAmount = 0;
      let totalTax = 0;
      const calculateTotal = (items, priceKey) => {
        return items.reduce(
          (acc, item) => {
            const price = Number(item?.[priceKey]) || 0;
            const taxAmount = price * ((Number(item?.tax) || 0) / 100);
            return {
              totalAmount: acc.totalAmount + price + taxAmount,
              totalTax: acc.totalTax + (item?.tax || 0),
            };
          },
          { totalAmount: 0, totalTax: 0 }
        );
      };

      if (healthScreeningItems.length > 0) {
        // Health Screening Calculation
        const { totalAmount: hsAmount, totalTax: hsTax } = calculateTotal(
          healthScreeningItems,
          "healthScreeningAmount"
        );
        totalAmount = hsAmount;
        totalTax = hsTax;
      } else if (physiotherapyItems.length > 0) {
        // Physiotherapy Calculation
        const { totalAmount: ptAmount, totalTax: ptTax } = calculateTotal(
          physiotherapyItems,
          "price"
        );
        totalAmount = ptAmount;
        totalTax = ptTax;
      } else if (radioServices.length > 0) {
        // Radiology Services Calculation
        radioServices.forEach((service) => {
          if (service?.radiologyServiceInfo?.serviceDetails?.length > 0) {
            service.radiologyServiceInfo.serviceDetails.forEach((detail) => {
              const price = Number(detail?.price) || 0;
              const taxAmount = price * ((Number(detail?.tax) || 0) / 100);
              totalAmount += price + taxAmount;
              totalTax += detail?.tax || 0;
            });
          }
        });
      } else if (healthPackageItems.length > 0) {
        // Health Package Calculation
        const { totalAmount: hpAmount, totalTax: hpTax } = calculateTotal(
          healthPackageItems,
          "healthPackageAmount"
        );
        totalAmount = hpAmount;
        totalTax = hpTax;
      } else if (skinServices.length > 0) {
        // Skin Services Calculation
        const { totalAmount: ssAmount, totalTax: ssTax } = calculateTotal(
          skinServices,
          isClient ? "client_price" : "pt_price"
        );
        totalAmount = ssAmount;
        totalTax = ssTax;
      } else {
        // Other Services Calculation
        const { totalAmount: otherAmount, totalTax: otherTax } = calculateTotal(
          otherServices,
          "price"
        );
        totalAmount = otherAmount;
        totalTax = otherTax;
      }

      const cgstSgst = (totalTax / 2).toFixed(2);
      setFormData((prev) => ({
        ...prev,
        totalAmount: totalAmount.toFixed(2),
        totalAfterDiscount: totalAmount.toFixed(2),
        dueAmount: totalAmount.toFixed(2),
        cgst: cgstSgst,
        sgst: cgstSgst,
      }));
    }
  }, [salesById]);

  const invoiceHeaderRight = () => (
    <div className={classNames("row mb-4", styles.InvoiceHeading)}>
      <div className="mt-1 col-xl-6">
        <h6>From:</h6>
        <div className="mt-1">
          <strong>
            {
              salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.city?.name
            }
          </strong>
        </div>
        <div className="mt-1">
          {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address ? (
            <>
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.plot &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.plot}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.area &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.area}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.street &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.street}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.landMark &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.landMark}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.city?.name &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.city.name}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.state?.name &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.state.name}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.country?.name &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.country.name}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.pin &&
                salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                  ?.pin}
            </>
          ) : (
            "N/A"
          )}
        </div>
        <div className="mt-1">
          Name:{salesById?.sale?.appointmentId[0]?.patientId?.userId?.name}
        </div>
        <div className="mt-1">
          Email: {salesById?.sale?.appointmentId[0]?.patientId?.userId?.email}
        </div>
        <div className="mt-1">
          Phone: {salesById?.sale?.appointmentId[0]?.patientId?.userId?.phone}
        </div>
      </div>
      <div className="mt-1 col-sm-6 d-flex justify-content-end">
        <div className="row">
          <div className="col-sm-9">
            <div className="brand-logo mb-3">
              {clinic?.hospitalInfo?.headerLogo && (
                <img
                  src={clinic?.hospitalInfo?.headerLogo}
                  className="img-fluid width110"
                  alt=""
                  style={{ width: "270px" }}
                />
              )}
            </div>
            <span className="d-block">
              {clinic?.clinicAddress ? (
                <>
                  {clinic.clinicAddress.plot &&
                    `${clinic.clinicAddress.plot}, `}
                  {clinic.clinicAddress.area &&
                    `${clinic.clinicAddress.area}, `}
                  {clinic.clinicAddress.street &&
                    `${clinic.clinicAddress.street}, `}
                  {clinic.clinicAddress.landMark &&
                    `${clinic.clinicAddress.landMark}, `}
                  {clinic.clinicAddress.city?.name &&
                    `${clinic.clinicAddress.city.name}, `}
                  {clinic?.clinicAddress?.state?.name &&
                    `${clinic.clinicAddress.state.name}, `}
                  {clinic.clinicAddress.country?.name &&
                    `${clinic.clinicAddress.country.name}, `}
                  {clinic.clinicAddress.pin && clinic.clinicAddress.pin}
                </>
              ) : (
                "N/A"
              )}
            </span>
            <small className="d-block mt-1">
              <strong>Website</strong> : {clinic?.hospitalInfo?.web_address}
            </small>
            <small className="d-block mt-1">
              <strong>Contact No</strong> :{clinic?.phone}
            </small>
            <small className="d-block mt-1">
              <strong>GSTIN</strong> : {clinic?.accountInfo?.gst}
            </small>
          </div>
          <div className="col-sm-3 text-end">
            <img
              src={clinic?.hospitalInfo?.ISOLogo}
              className="img-fluid width110"
              alt=""
              style={{ width: "70px" }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const invoiceHeaderLeft = () => (
    <div className={classNames("row mb-4", styles.InvoiceHeading)}>
      <div className="mt-1 col-sm-6">
        <div className="brand-logo mb-3">
          <div
            style={{
              backgroundImage: `url(${clinic?.hospitalInfo?.headerLogo})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              width: "350px",
              height: "70px",
              objectFit: "cover",
            }}
          ></div>
        </div>
        <span className="d-block">
          {clinic?.clinicAddress ? (
            <>
              {clinic.clinicAddress.plot && `${clinic.clinicAddress.plot}, `}
              {clinic.clinicAddress.area && `${clinic.clinicAddress.area}, `}
              {clinic.clinicAddress.street &&
                `${clinic.clinicAddress.street}, `}
              {clinic.clinicAddress.landMark &&
                `${clinic.clinicAddress.landMark}, `}
              {clinic.clinicAddress.city?.name &&
                `${clinic.clinicAddress.city.name}, `}
              {clinic?.clinicAddress?.state?.name &&
                `${clinic.clinicAddress.state.name}, `}
              {clinic.clinicAddress.country?.name &&
                `${clinic.clinicAddress.country.name}, `}
              {clinic.clinicAddress.pin && clinic.clinicAddress.pin}
            </>
          ) : (
            "N/A"
          )}
        </span>
        <small className="d-block mt-1">
          <strong>Website</strong> : {clinic?.hospitalInfo?.web_address}
        </small>
        <small className="d-block mt-1">
          <strong>Contact No</strong> :{clinic?.phone}
        </small>
        <small className="d-block mt-1">
          <strong>GSTIN</strong> : {clinic?.accountInfo?.gst}
        </small>
      </div>
      <div className="mt-1 col-xl-6 d-flex justify-content-end">
        <div className="row">
          <div className="mt-1 col-sm-9">
            <h6>From:</h6>
            <div className="mt-1">
              <strong>
                {
                  salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                    ?.city?.name
                }
              </strong>
            </div>
            <div className="mt-1">
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address ? (
                <>
                  {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                    ?.plot &&
                    `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.plot}, `}
                  {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                    ?.area &&
                    `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.area}, `}
                  {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                    ?.street &&
                    `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.street}, `}
                  {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                    ?.landMark &&
                    `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.landMark}, `}
                  {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                    ?.city?.name &&
                    `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.city.name}, `}
                  {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                    ?.state?.name &&
                    `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.state.name}, `}
                  {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                    ?.country?.name &&
                    `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.country.name}, `}
                  {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                    ?.pin &&
                    salesById?.sale?.appointmentId[0]?.patientId?.userId
                      ?.address?.pin}
                </>
              ) : (
                "N/A"
              )}
            </div>
            <div className="mt-1">
              Name:{salesById?.sale?.appointmentId[0]?.patientId?.userId?.name}
            </div>
            <div className="mt-1">
              Email:{" "}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.email}
            </div>
            <div className="mt-1">
              Phone:{" "}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.phone}
            </div>
          </div>
          <div className="col-sm-3 text-end">
            {clinic?.hospitalInfo?.displayISOlogo && (
              <div
                style={{
                  backgroundImage: `url(${clinic.hospitalInfo.ISOLogo})`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  width: "52px",
                  height: "60px",
                  objectFit: "cover",
                }}
              ></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const invoiceHeaderCenter = () => (
    <div className={classNames("row mb-4", styles.InvoiceHeading)}>
      <div className="mt-1 col-sm-9 d-flex justify-content-between">
        <div className="brand-logo mb-3">
          <img
            className="logo-abbr me-2"
            src={clinic?.hospitalInfo?.headerLogo}
            alt=""
          />
        </div>
      </div>
      <div className="mt-1 col-sm-3 text-end">
        {clinic?.hospitalInfo?.displayISOlogo && (
          <img
            src={clinic.hospitalInfo.ISOLogo}
            className="img-fluid width110"
            alt=""
            style={{ width: "70px" }}
          />
        )}
      </div>
      <div className="mt-1 col-sm-12">
        <span className="d-block">
          {clinic?.clinicAddress ? (
            <>
              {clinic.clinicAddress.plot && `${clinic.clinicAddress.plot}, `}
              {clinic.clinicAddress.area && `${clinic.clinicAddress.area}, `}
              {clinic.clinicAddress.street &&
                `${clinic.clinicAddress.street}, `}
              {clinic.clinicAddress.landMark &&
                `${clinic.clinicAddress.landMark}, `}
              {clinic.clinicAddress.city?.name &&
                `${clinic.clinicAddress.city.name}, `}
              {clinic?.clinicAddress?.state?.name &&
                `${clinic.clinicAddress.state.name}, `}
              {clinic.clinicAddress.country?.name &&
                `${clinic.clinicAddress.country.name}, `}
              {clinic.clinicAddress.pin && clinic.clinicAddress.pin}
            </>
          ) : (
            "N/A"
          )}
        </span>
        <small className="d-block mt-1">
          <strong>Website</strong> : {clinic?.hospitalInfo?.web_address}
        </small>
        <small className="d-block mt-1">
          <strong>Contact No</strong> :{clinic?.phone}
        </small>
        <small className="d-block mt-1">
          <strong>GSTIN</strong> : {clinic?.accountInfo?.gst}
        </small>
      </div>
      <div className=" col-sm-12 mt-3 mb-3">
        <div style={{ backgroundColor: "#40be47", padding: "1px" }}></div>
      </div>
      <div className="mt-1 col-xl-12">
        <small className="mt-1 ms-2">
          <strong>From: </strong>
          {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.city
            ?.name ? (
            <>
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.plot &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.plot}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.area &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.area}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.street &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.street}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.landMark &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.landMark}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.city?.name &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.city.name}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.state?.name &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.state.name}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.country?.name &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.country.name}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.pin &&
                salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                  ?.pin}
            </>
          ) : (
            "N/A"
          )}
        </small>
        <small className="mt-1 ms-2">
          <strong>Name: </strong>
          {salesById?.sale?.appointmentId[0]?.patientId?.userId?.name}
        </small>
        <small className="mt-1 ms-2">
          <strong>Email: </strong>
          {salesById?.sale?.appointmentId[0]?.patientId?.userId?.email}
        </small>
        ,
        <small className="mt-1 ms-2">
          <strong>Phone: </strong>
          {salesById?.sale?.appointmentId[0]?.patientId?.userId?.phone}
        </small>
        ,
      </div>
    </div>
  );
  const invoiceHeaderFullWidth = () => (
    <div className={classNames("row mb-4", styles.InvoiceHeading)}>
      <div className="mt-1 col-sm-5">
        <div className="brand-logo mb-3">
          <img
            className="logo-abbr me-2"
            src={clinic?.hospitalInfo?.headerLogo}
            alt=""
          />
        </div>
      </div>
      <div className="mt-1 col-sm-5">
        <span className="d-block">
          {clinic?.clinicAddress ? (
            <>
              {clinic.clinicAddress.plot && `${clinic.clinicAddress.plot}, `}
              {clinic.clinicAddress.area && `${clinic.clinicAddress.area}, `}
              {clinic.clinicAddress.street &&
                `${clinic.clinicAddress.street}, `}
              {clinic.clinicAddress.landMark &&
                `${clinic.clinicAddress.landMark}, `}
              {clinic.clinicAddress.city?.name &&
                `${clinic.clinicAddress.city.name}, `}
              {clinic?.clinicAddress?.state?.name &&
                `${clinic.clinicAddress.state.name}, `}
              {clinic.clinicAddress.country?.name &&
                `${clinic.clinicAddress.country.name}, `}
              {clinic.clinicAddress.pin && clinic.clinicAddress.pin}
            </>
          ) : (
            "N/A"
          )}
        </span>
        <small className="d-block mt-1">
          <strong>Website</strong> : {clinic?.hospitalInfo?.web_address}
        </small>
        <small className="d-block mt-1">
          <strong>Contact No</strong> :{clinic?.phone}
        </small>
        <small className="d-block mt-1">
          <strong>GSTIN</strong> : {clinic?.accountInfo?.gst}
        </small>
      </div>
      <div className="mt-1 col-sm-2 text-end">
        {clinic?.hospitalInfo?.displayISOlogo && (
          <img
            src={clinic?.hospitalInfo?.ISOLogo}
            className="img-fluid width110"
            alt=""
            style={{ width: "70px" }}
          />
        )}
      </div>

      <div className=" col-sm-12 mt-3 mb-3">
        <div style={{ backgroundColor: "#40be47", padding: "1px" }}></div>
      </div>
      <div className="mt-1 col-xl-12">
        <small className="mt-1 ms-2">
          <strong>From: </strong>{" "}
          {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.city
            ?.name ? (
            <>
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.plot &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.plot}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.area &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.area}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.street &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.street}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.landMark &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.landMark}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.city?.name &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.city.name}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.state?.name &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.state.name}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.country?.name &&
                `${salesById?.sale?.appointmentId[0]?.patientId?.userId?.address?.country.name}, `}
              {salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                ?.pin &&
                salesById?.sale?.appointmentId[0]?.patientId?.userId?.address
                  ?.pin}
            </>
          ) : (
            "N/A"
          )}
        </small>
        <small className="mt-1 ms-2">
          <strong>Name: </strong>{" "}
          {salesById?.sale?.appointmentId[0]?.patientId?.userId?.name}
        </small>
        <small className="mt-1 ms-2">
          <strong>Email: </strong>{" "}
          {salesById?.sale?.appointmentId[0]?.patientId?.userId?.email}
        </small>
        ,
        <small className="mt-1 ms-2">
          <strong>Phone: </strong>{" "}
          {salesById?.sale?.appointmentId[0]?.patientId?.userId?.phone}
        </small>
        ,
      </div>
    </div>
  );
  return (
    <Row
      className={showInvoice ? styles.InvoiceWrapper : styles.InvoiceWrapperTwo}
    >
      <div className="col-lg-12">
        <div
          className={classNames(styles.InvoiceCard, "card")}
          ref={invoiceRef}
          style={{ backgroundImage: `url(${clinic?.hospitalInfo?.waterMark})` }}
        >
          <div
            className="card-body mb-auto position-relative"
            style={{ minHeight: "200vh" }}
          >
            {clinic?.hospitalInfo?.headerAlignment === "right" &&
              invoiceHeaderRight()}
            {clinic?.hospitalInfo?.headerAlignment === "left" &&
              invoiceHeaderLeft()}
            {clinic?.hospitalInfo?.headerAlignment === "center" &&
              invoiceHeaderCenter()}
            {clinic?.hospitalInfo?.headerAlignment === "headerFullWidth" &&
              invoiceHeaderFullWidth()}
            <div className="table-responsive">
              <table className="table">
                <thead className={styles.thead}>
                  <tr className={styles.tr}>
                    <th className={classNames(styles.th, "center")}>Sl No</th>
                    <th className={classNames(styles.th, "center")}>
                      Item Name
                    </th>
                    <th className={classNames(styles.th, "center")}>Rate</th>
                    <th className={classNames(styles.th, "center")}>Tax</th>
                    <th className={classNames(styles.th, "center")}>SGST</th>
                    <th className={classNames(styles.th, "center")}>CGST</th>
                    <th className={classNames(styles.th, "right")}>Amount</th>
                  </tr>
                </thead>

                <tbody>
                  {(() => {
                    let slNo = 1;
                    return salesById?.sale?.invoiceId?.flatMap(
                      (invoice, invIndex) => {
                        const appointmentDetails =
                          invoice?.appointmentDetails || {};
                        const isClient = invoice?.isClient === "Yes";
                        const rows = [];

                        const addRow = (name, rate, tax) => {
                          const sgst = tax / 2;
                          const cgst = tax / 2;
                          const total = rate + (rate * tax) / 100;
                          rows.push(
                            <tr key={`row-${invIndex}-${slNo}`}>
                              <td className="center">{slNo++}</td>
                              <td className="left">{name}</td>
                              <td className="right">{rate.toFixed(2)}</td>
                              <td className="center">{tax.toFixed(2)}%</td>
                              <td className="center">{sgst.toFixed(2)}%</td>
                              <td className="center">{cgst.toFixed(2)}%</td>
                              <td className="right">{total.toFixed(2)}</td>
                            </tr>
                          );
                        };

                        const hasPrimaryServices =
                          appointmentDetails?.consultationDetails?.length ||
                          appointmentDetails?.physiotherapyService?.length ||
                          appointmentDetails?.radioServices?.length ||
                          appointmentDetails?.healthScreeningDetails?.length ||
                          appointmentDetails?.healthPackageDetails?.length;

                        if (hasPrimaryServices) {
                          appointmentDetails?.consultationDetails?.forEach(
                            (item) =>
                              addRow(
                                salesById?.sale?.appointmentId?.[invIndex]
                                  ?.physicianId?.userId?.name || "Consultation",
                                parseFloat(item?.price || 0),
                                0
                              )
                          );

                          appointmentDetails?.physiotherapyService?.forEach(
                            (item) =>
                              addRow(
                                item?.physioId?.serviceDetailName || "N/A",
                                parseFloat(item?.price || 0),
                                parseFloat(item?.physioId?.tax || 0)
                              )
                          );

                          if (!appointmentDetails?.healthScreeningDetails) {
                            appointmentDetails?.radioServices?.forEach(
                              (item) => {
                                const serviceName =
                                  item?.radiologyServiceInfo?.serviceName ||
                                  "N/A";
                                const details =
                                  item?.radiologyServiceInfo?.serviceDetails ||
                                  [];

                                details.forEach((detail) => {
                                  addRow(
                                    `${serviceName} - ${
                                      detail?.radioId?.serviceDetailsName ||
                                      detail?.serviceName ||
                                      "N/A"
                                    }`,
                                    parseFloat(detail?.price || 0),
                                    parseFloat(detail?.tax || item?.tax || 0)
                                  );
                                });
                              }
                            );
                          }

                          appointmentDetails?.healthScreeningDetails?.forEach(
                            (item) =>
                              addRow(
                                item?.healthScreeningName || "N/A",
                                parseFloat(item?.healthScreeningAmount || 0),
                                parseFloat(item?.tax || 0)
                              )
                          );

                          appointmentDetails?.healthPackageDetails?.forEach(
                            (item) =>
                              addRow(
                                item?.healthPackageName || "N/A",
                                parseFloat(item?.healthPackageAmount || 0),
                                parseFloat(item?.tax || 0)
                              )
                          );
                        } else {
                          const getTaxFromItem = (item) => {
                            if (!item || typeof item !== "object") return 0;
                            if (typeof item?.tax === "number") return item.tax;
                            for (const key of Object.keys(item)) {
                              if (
                                item[key] &&
                                typeof item[key] === "object" &&
                                "tax" in item[key]
                              ) {
                                return Number(item[key].tax) || 0;
                              }
                            }

                            return 0;
                          };
                          const idMapping = {
                            tests: { id: "testId", nameKey: "testName" },
                            profiles: {
                              id: "profileId",
                              nameKey: "profileName",
                            },
                            dentalServices: {
                              id: "dentalId",
                              nameKey: "serviceName",
                            },
                            hairServices: {
                              id: "hairId",
                              nameKey: "serviceName",
                            },
                            skinServices: {
                              id: "skinId",
                              nameKey: "serviceName",
                            },
                            generalServices: {
                              id: "generalId",
                              nameKey: "serviceName",
                            },
                          };

                          [
                            "tests",
                            "profiles",
                            "dentalServices",
                            "hairServices",
                            "skinServices",
                            "generalServices",
                          ].forEach((key) => {
                            const mapping = idMapping[key];
                            appointmentDetails?.[key]?.forEach((item) => {
                              const serviceName =
                                item?.[mapping.id]?.[mapping.nameKey] ||
                                item?.serviceName ||
                                "N/A";

                              addRow(
                                serviceName,
                                parseFloat(item?.price || 0),
                                getTaxFromItem(item)
                              );
                            });
                          });
                        }

                        return rows;
                      }
                    );
                  })()}
                </tbody>
              </table>
            </div>
            <div className="row">
              <div className="col-sm-4"> </div>
              <div className="col-sm-4 ms-auto">
                <table
                  className={classNames(
                    styles.subTotalTable,
                    "table table-clear"
                  )}
                >
                  <tbody>
                    <tr>
                      <td className={classNames(styles.td, "left")}>
                        <strong> Amount</strong>
                      </td>
                      <td className={classNames(styles.td, "right")}>
                        <strong>{salesById?.sale?.totalPrice}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className={classNames(styles.td, "left")}>
                        <strong> Discount</strong>
                      </td>
                      <td className={classNames(styles.td, "right")}>
                        <strong>{salesById?.sale?.discountPrice}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className={classNames(styles.td, "left")}>
                        <strong>Paid Amount</strong>
                      </td>
                      <td className={classNames(styles.td, "right")}>
                        <strong>
                          {Array.isArray(salesById?.sale?.paymentDetails)
                            ? salesById?.sale?.paymentDetails.reduce(
                                (sum, payment) =>
                                  sum + (payment.paidAmount || 0),
                                0
                              )
                            : 0}
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td className={classNames(styles.td, "left")}>
                        <strong>Balance Due </strong>
                      </td>
                      <td className={classNames(styles.td, "right")}>
                        <strong>{salesById?.sale?.TotalDueAmount}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div style={{ position: "absolute", bottom: "0", width: "100%" }}>
            <div className="pb-2 ps-5 pe-0 d-flex justify-content-between position-relative">
              <div>
                <b
                  style={{
                    fontSize: "0.9rem",
                    position: "absolute",
                    bottom: "0.6rem",
                  }}
                >
                  Billed By : SimpleeKare
                </b>
              </div>
              <div style={{ paddingRight: 35, marginTop: 10 }}>
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <div
                    style={{
                      backgroundImage: `url(${clinic?.hospitalInfo?.authorizedSignature})`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                      width: "150px",
                      height: "70px",
                      objectFit: "cover",
                    }}
                  ></div>
                  <b style={{ fontSize: "0.95rem" }}>Signature</b>
                </div>
              </div>
            </div>
            <div className="row pb-4 ps-5 pe-5">
              <div style={{ backgroundColor: "#40be47", padding: "1px" }}></div>
              <div className="col-sm-12 p-0 mt-4">
                <div className="brand-logo d-flex align-items-center justify-content-between">
                  <img
                    className="logo-abbr me-2"
                    src={qrcode}
                    alt=""
                    style={{ width: "80px" }}
                  />
                  <b style={{ fontSize: "0.85rem" }}>
                    * No Extra Amount to be paid apart from Billing Amount.
                  </b>
                </div>
              </div>
            </div>
          </div>
        </div>
        <button
          className="btn btn-primary mt-3 text-white"
          onClick={downloadInvoice}
        >
          Download PDF
        </button>
      </div>
    </Row>
  );
};

export default ReceiptInvoice;
